import { useState, useEffect } from "react";
import ReportRevenueBoard from "./ReportRevenueBoard";
import { Input, DatePicker, Pagination, Select, message, ConfigProvider } from "antd";
import { PAGINATION_PAGE_OPTIONS, SALES_REPORT_PAYMENT_TYPE } from "../../../constant/constants";
import ReportTable from "./ReportTable";
import { ReportService } from "../../../services/reports-services";
import dayjs from "dayjs";
import useUserStore from "../../../stores/store";
import { isDate1GreaterThanDate2 } from "../../../utils/Common";
import { useNavigate } from "react-router-dom";
import MultiCheckedSelectBox from "../../common/MultiCheckedSelectBox";

export default function SalesReportComponent({
  paymentReceivedOptions,
  takeoutOptions,
  summaryData,
  summationData = {},
  reportData,
  setSalesFilter,
  handleFilterApply,
  page,
  perPage,
  setPerPage,
  total,
  handlePageChange,
  setIsFilterClear,
  handleDownload,
  loading,
}) {
  const { showNotification } = useUserStore();
  const [orderNo, setOrderNo] = useState("");
  const [fromDateTime, setFromDateTime] = useState("");
  const [toDateTime, setToDateTime] = useState("");
  const [paymentType, setPaymentType] = useState("");
  const navigate = useNavigate();
  const [selectedPayments, setSelectedPayments] = useState([]);
  const [selectedOrderTypes, setSelectedOrderTypes] = useState([]);

  useEffect(() => {
    setSalesFilter((prev) => ({
      ...prev,
      orderNo,
      fromDateTime,
      toDateTime,
      paymentReceivedTypes: selectedPayments,
      takeoutTypes: selectedOrderTypes,
      page,
      perPage,
    }));
  }, [orderNo, fromDateTime, toDateTime, selectedPayments, selectedOrderTypes, page, perPage]);


  function onHandleOrderNo(e) {
    const val = e.target.value;
    if (ReportService.isTextValid(val)) {
      return;
    }
    setOrderNo(val);
  }

  function onHandleFromDate(date, dateString) {
    if (isDate1GreaterThanDate2(dateString, toDateTime)) {
      showNotification("From Date time cannot be greater than To Date time.");
      return;
    }

    setFromDateTime(dateString);
  }

  function onHandleToDate(date, dateString) {
    if (date) {
      const selectedTime = date.format("HH:mm:ss");
      const timeToSet = selectedTime === "00:00:00" ? "23:59:59" : selectedTime;
      const tempToDate = date.format(`YYYY-MM-DD ${timeToSet}`);

      if (fromDateTime != "" && !(new Date(tempToDate) >= new Date(fromDateTime))) {
        message.warning("This date must be grater than from date.");
        setToDateTime("");
        return;
      }
      setToDateTime(tempToDate);
    } else {
      setToDateTime("");
    }
  }

  function onHandlePaymentType(value) {
    setPaymentType(value);
  }

  function onHandleClearFilter() {
    setIsFilterClear(true);
    setOrderNo("");
    setFromDateTime("");
    setToDateTime("");
    setPaymentType("");

    setSelectedPayments([]);
    setSelectedOrderTypes([]);

    setSalesFilter((prev) => ({
      restaurantId: prev.restaurantId,
      page: 1,
      perPage: 10,
    }));
  }

  function onHandleView(reportId) {
    if (reportId == null) {
      showNotification("Something went wrong. Try again later.");
      return;
    };
    let url = `/reports/orders/${reportId}/details`;
    navigate(url);
  }

  return (
    <section>
      {/* revenue board */}
      <ReportRevenueBoard summaryData={summaryData} data={reportData} handleDownload={handleDownload} context="sales" />

      {/* filters */}
      <ConfigProvider
        theme={{
          components: {
            DatePicker: {
              zIndexPopup: 1,
            },
            Select: {
              zIndexPopup: 1,
            },
          },
        }}
      >
        <div className="flex items-center gap-x-[20px] space-between w-full mt-[20px] mb-[31px]">
          <div className="flex items-center space-x-[15px] items-center w-full">
            <div className="h-[48px] w-[20%]">
              <Input
                className="h-full w-full"
                value={orderNo || ""}
                onChange={onHandleOrderNo}
                placeholder="Order No"
              />
            </div>
            <div className="h-[48px] w-[20%]">
              <DatePicker
                className={"h-full w-full"}
                showTime={{ format: "HH:mm", showSecond: false }}
                format="YYYY-MM-DD HH:mm"
                onChange={onHandleFromDate}
                placeholder="From Date / Time"
                needConfirm
                value={fromDateTime ? dayjs(fromDateTime) : null}
                disabledDate={(current) => current && current > dayjs().endOf("day")}
              />
            </div>
            <div className="h-[48px] w-[20%]">
              <DatePicker
                className={"h-full w-full"}
                showTime={{ format: "HH:mm", showSecond: false }}
                format="YYYY-MM-DD HH:mm"
                onChange={onHandleToDate}
                placeholder="To Date / Time"
                needConfirm
                value={toDateTime ? dayjs(toDateTime) : null}
                disabledDate={(current) => {
                  if (!fromDateTime) {
                    return current && current > dayjs().endOf("day");
                  }
                  return (current && current < dayjs(fromDateTime).startOf("day")) || current > dayjs().endOf("day");
                }}
              />
            </div>
            <div className="h-[48px] w-[23%]">
              <MultiCheckedSelectBox
                context={'payment'}
                placeholderText={'Payment'}
                boxClasses={'h-[48px]'}
                dropdownCardClasses={'mt-3'}
                defaultSelects={[]}
                options={paymentReceivedOptions}
                selectedOptions={selectedPayments}
                setSelectedOptions={setSelectedPayments}
              />
            </div>

            <div className="h-[48px] w-[20%]">
              <MultiCheckedSelectBox
                context={'orderType'}
                placeholderText={'Order Type'}
                boxClasses={'h-[48px]'}
                dropdownCardClasses={'mt-3'}
                defaultSelects={[]}
                options={takeoutOptions}
                selectedOptions={selectedOrderTypes}
                setSelectedOptions={setSelectedOrderTypes}
              />
            </div>
          </div>
          <div className="flex items-center space-x-[20px] h-[48px] w-[200px]">
            <button
              className="w-full h-full bg-black text-[14px] font-bold leading-[16px] text-white rounded-[5px] w-[50%]"
              onClick={onHandleClearFilter}
            >
              Clear
            </button>
            <button
              className="w-full h-full bg-[#FCBD10] text-[14px] font-bold leading-[16px] text-black rounded-[5px] w-[50%]"
              onClick={handleFilterApply}
            >
              Apply
            </button>
          </div>
        </div>
      </ConfigProvider>

      {/* Reports revenue */}
      <div className="mb-3">
        <ReportRevenueBoard summaryData={summationData} context="sales" showDownloadBtn={false} />
      </div>
      {/* sales report table */}
      <div>
        <ReportTable
          paymentReceivedData={paymentReceivedOptions}
          data={reportData}
          context={"sales"}
          page={page}
          perPage={perPage}
          isLoading={loading}
          actions={['view']}
          handleView={onHandleView} />

        <div className="mt-[20px] mb-[50px] report-pagination relative custom-pagination-options">
          <Pagination
            showSizeChanger
            onChange={handlePageChange}
            onShowSizeChange={(current, size) => {
              setPerPage(size);
            }}
            current={page}
            pageSize={perPage}
            total={total}
            pageSizeOptions={PAGINATION_PAGE_OPTIONS}
            locale={{ items_per_page: "" }}
            showSearch={false}
            showQuickJumper={false}
          />

          <div
            className="absolute h-[48px] flex items-center space-x-[40px] 
          top-0 right-[122px] text-[18px] font-medium text-[#7D7D7D] mr-3"
          >
            <div>Total: {total}</div>
            <div>Row </div>
          </div>
        </div>
      </div>
    </section>
  );
}
